/* eslint-disable no-restricted-globals */
import { RouteComponentProps } from "@reach/router";
import {
  FormEvent,
  FunctionComponent,
  useContext,
  useEffect,
  useState
} from "react";
import Button from "../components/button/Button";
import Table, { Column } from "../components/table/Table";
import LayoutContext from "../context/layout-context";
import { initialPaginationData } from "../utils/constants";
import { WalletCreate, WalletCreations } from "../utils/helpers/types";
import ContextMenu, {
  ContextOption
} from "../components/context-menu/ContextMenu";
import { BooleanFilter } from "../utils/helpers/types-helpers";
import styles from "./self-service.module.scss";
import Modal, { ModalProps } from "../components/modal/Modal";
import { resources } from "../utils/resources";
import Input from "../components/input/Input";
import style from "./history.module.scss";
import {
  createWallet,
  deleteWallet,
  getWallet,
  getWallets,
  updateWallet
} from "../utils/helpers/data/wallet";

const WalletConfiguration: FunctionComponent<RouteComponentProps> = () => {
  const [loading, setLoading] = useState(false);
  const [reset, setReset] = useState(false);
  const [pagination, setPagination] = useState({ ...initialPaginationData });
  const [walletOnView, setWalletOnView] = useState<WalletCreations | null>(
    null
  );
  const { notify, confirm } = useContext(LayoutContext);
  const [wallet, setWallet] = useState<WalletCreations[]>([]);
  const [walletOnEdit, setWalletOnEdit] = useState<WalletCreations | null>(
    null
  );
  const [showAddEditModal, setShowAddEditModal] = useState(false);

  const [name, setName] = useState<string>("");

  const showResetFilter = name;

  const resetFilters = () => {
    setName("");

    setPagination({ ...pagination, pageNumber: 1 });
    setReset(true);
  };

  const displayAddEditModal = async (
    walletCreation: WalletCreations | null
  ) => {
    if (walletCreation) {
      setLoading(true);
      const { data, error, message } = await getWallet(walletCreation.id);
      setLoading(false);
      if (error || !data) {
        notify("error", `Unable to fetch wallet details: ${message}`);
        return;
      }
      setWalletOnEdit(data);
    } else {
      setWalletOnEdit(null);
    }
    setShowAddEditModal(true);
  };

  const dismissAddEditModal = () => {
    setWalletOnEdit(null);
    setShowAddEditModal(false);
  };

  const fetchWallet = async (
    pageNumber = pagination.pageNumber,
    pageSize = pagination.pageSize
  ) => {
    setLoading(true);
    const { data, error, message } = await getWallets(
      pageNumber,
      pageSize,
      name
    );
    setLoading(false);

    if (error || !data) {
      notify("error", `Unable to fetch wallets: ${message}`);
      return;
    }

    setWallet(data.records);
    setPagination({
      ...pagination,
      pageNumber,
      total: data.total
    });

    setReset(false);
  };

  const handleViewReport = async () => {
    setLoading(true);
    try {
      setPagination({ ...pagination, pageNumber: 1 });
      await fetchWallet(pagination.pageNumber);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      notify("error", "An error occurred while fetching wallet.", error);
    }
  };

  useEffect(() => {
    fetchWallet();
  }, [pagination.pageSize]);

  useEffect(() => {
    if (!reset) {
      fetchWallet();
    } else {
      // Reset logic
      setPagination({ ...initialPaginationData });
      setReset(false);
    }
  }, [reset]);

  const handleOperation = async (operation: any, id: any) => {
    setLoading(true);
    let result;

    switch (operation) {
      case "delete":
        result = await deleteWallet(id);
        break;

      case "view":
        result = await getWallet(id);
        if (!result.error) {
          setWalletOnView(result.data);
        }
        break;
      default:
        setLoading(false);
        return;
    }

    setLoading(false);

    if (result.error) {
      notify("error", `Unable to ${operation} wallet: ${result.message}`);
      return;
    }

    notify("success", `wallet ${operation}d successfully`);
  };

  const handleDelete = async (wallet: WalletCreations) => {
    confirm({
      title: "Are you sure you want to delete this wallet?",
      body: "This action cannot be undone.",
      onOk: async () => {
        setLoading(true);
        const { error, message } = await deleteWallet(wallet.id);
        setLoading(false);

        if (error) {
          notify("error", `Unable to delete wallet: ${message}`);
          return;
        }

        notify("success", "Wallet deleted successfully");
        await fetchWallet();
      }
    });
  };

  const formatDate = (date: string): string => {
    const formattedDate = new Date(date)
      .toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
      })
      .replace(/\//g, "-");
    return formattedDate;
  };
  const columns: Column<WalletCreations>[] = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name"
    },
    {
      title: "Base URL",
      dataIndex: "baseUrl",
      key: "baseUrl"
    },
    {
      title: "Version",
      dataIndex: "versions",
      key: "versions"
    },
    {
      title: "Created By",
      dataIndex: "createdBy",
      key: "createdBy"
    },
    {
      title: "Created On",
      dataIndex: "createdOn",
      key: "createdOn",
      render: (createdOn: string) => formatDate(createdOn)
    },

    {
      title: "",
      render: (_: unknown, wallet: WalletCreations) => {
        const options: ContextOption[] = [
          {
            label: "View Details",
            onClick: () => {
              handleOperation("view", wallet.id);
            }
          },
          {
            label: "Edit",
            onClick: () => {
              displayAddEditModal(wallet);
            }
          },
          {
            label: "Delete",
            onClick: () => handleDelete(wallet)
          }
        ].filter(BooleanFilter) as ContextOption[];
        return <ContextMenu options={options} />;
      }
    }
  ];
  return (
    <main>
      <h1 className="title vertical-margin spaced thin">Wallet</h1>

      <div className="vertical-margin spaced">
        <div className={style.cont}>
          <div className={style.container}>
            <div className={style.cont1}>
              <div className="input-group half-width">
                <Input
                  value={name}
                  onChange={(value: string) => setName(value)}
                  placeholder="Enter name "
                />
              </div>
            </div>

            <div className={style.inputView}>
              <Button
                type="accent"
                size="large"
                className="strong no-pdf-print"
                onClick={handleViewReport}
              >
                Filter record
              </Button>

              <span className={style.button} />
            </div>
          </div>
        </div>
        {showResetFilter && (
          <Button type="accent" size="small" onClick={resetFilters}>
            RESET FILTERS
          </Button>
        )}
      </div>

      <br />
      <br />

      <div className="vertical-margin flex end">
        <Button onClick={() => displayAddEditModal(null)}>Create Wallet</Button>
      </div>

      <Table
        dataSource={wallet || []}
        columns={columns}
        pagination={pagination}
        onPageChange={fetchWallet}
        onPageSizeChange={pageSize =>
          setPagination({ ...pagination, pageSize, pageNumber: 1 })
        }
        loading={loading}
        className="smaller"
      />

      <WalletViewModal
        wallet={walletOnView}
        cancel={() => setWalletOnView(null)}
        visible={Boolean(walletOnView)}
      />

      <AddEditModal
        visible={showAddEditModal}
        walletOnEdit={walletOnEdit}
        cancel={dismissAddEditModal}
        refresh={(stayOnPage: any) =>
          fetchWallet(stayOnPage ? pagination.pageNumber : 1)
        }
        fetchData={fetchWallet}
      />
    </main>
  );
};

const blankappService: WalletCreate = {
  name: "",
  className: "",
  versions: "",
  baseUrl: "",
  balancePath: "",
  placeLienPath: "",
  debitLienPath: "",
  debitPath: "",
  reversalPath: "",
  probePath: ""
};

const AddEditModal: FunctionComponent<
  ModalProps & {
    walletOnEdit: WalletCreations | null;
    refresh: (stayOnPage: boolean) => void;
    cancel: () => void;
    fetchData: () => void;
  }
> = ({ visible, cancel, walletOnEdit, refresh, fetchData }) => {
  const [formData, setFormData] = useState<WalletCreate>({
    ...blankappService,
    ...walletOnEdit
  });

  const [isSaving, setIsSaving] = useState(false);
  const { notify } = useContext(LayoutContext);

  const handleChange = (key: keyof WalletCreate, value: unknown) => {
    setFormData(prevFormData => ({ ...prevFormData, [key]: value }));
  };

  useEffect(() => {
    setFormData(
      walletOnEdit
        ? { ...blankappService, ...walletOnEdit }
        : { ...blankappService }
    );
  }, [visible, walletOnEdit]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    setIsSaving(true);

    const nameRegex = /^[A-Za-z]+$/;

    if (!nameRegex.test(formData.name)) {
      notify("error", "Name must contain only letters");
      setIsSaving(false);
      return;
    }

    if (formData.name.length < 3) {
      notify("error", "Name must be at least 3 characters long");
      setIsSaving(false);
      return;
    }

    let id: number | undefined;

    if (walletOnEdit && typeof walletOnEdit.id === "number") {
      id = walletOnEdit.id;
    }

    if (id !== undefined) {
      const { error, message } = await updateWallet(id, {
        ...formData
      });

      if (error) {
        notify("error", `Unable to update wallet: ${message}`);
        setIsSaving(false);
        return;
      }

      notify("success", "Successfully updated wallet");
    } else {
      const { error, message } = await createWallet({
        ...formData
      });

      if (error) {
        notify("error", `Unable to create wallet: ${message}`);
        setIsSaving(false);
        return;
      }

      notify("success", "Successfully created wallet");
    }
    cancel?.();
    refresh(false);
    setIsSaving(false);
    fetchData(); // Call fetchData to refresh wallet data
  };

  return (
    <Modal cancel={cancel} visible={visible} size="small">
      <form onSubmit={handleSubmit}>
        <div className="input-group">
          <span className="question">Name</span>
          <Input
            value={formData.name}
            onChange={value => handleChange("name", value)}
            placeholder="Enter name"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Class Name</span>
          </div>
          <Input
            value={formData.className}
            onChange={value => handleChange("className", value)}
            placeholder="Enter class name"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Version</span>
          </div>
          <Input
            value={formData.versions}
            onChange={value => handleChange("versions", value)}
            placeholder="Enter versions"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Base Url</span>
          </div>
          <Input
            value={formData.baseUrl}
            onChange={value => handleChange("baseUrl", value)}
            placeholder="baseUrl"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Balance Path</span>
          </div>
          <Input
            value={formData.balancePath}
            onChange={value => handleChange("balancePath", value)}
            placeholder="Enter balance path"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Place Lien Path</span>
          </div>
          <Input
            value={formData.placeLienPath}
            onChange={value => handleChange("placeLienPath", value)}
            placeholder="Enter place lien path"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Debit Lien Path</span>
          </div>
          <Input
            value={formData.debitLienPath}
            onChange={value => handleChange("debitLienPath", value)}
            placeholder="Enter debit lien path"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Debit Path</span>
          </div>
          <Input
            value={formData.debitPath}
            onChange={value => handleChange("debitPath", value)}
            placeholder="Enter debit path"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Reversal Path</span>
          </div>
          <Input
            value={formData.reversalPath}
            onChange={value => handleChange("reversalPath", value)}
            placeholder="Enter reversal path"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Probe Path</span>
          </div>
          <Input
            value={formData.probePath}
            onChange={value => handleChange("probePath", value)}
            placeholder="Enter probe path"
            responsive
            required
          />
        </div>

        <br />
        <div className="flex spaced end vertical-margin">
          <Button type="accent" onClick={() => cancel?.()}>
            Cancel
          </Button>
          <Button buttonType="submit" loading={isSaving}>
            {walletOnEdit ? "Edit wallet" : "Create wallet"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

interface walletModalProps {
  wallet: WalletCreations | null;
  cancel: () => void;
  // refresh: () => void;
  visible: boolean;
}

const WalletViewModal: FunctionComponent<walletModalProps> = ({
  wallet: _wallet,
  cancel,
  visible
  // refresh
}) => {
  const [loading] = useState(false);
  const [wallet, setWallet] = useState<WalletCreations | null>(_wallet);

  useEffect(() => {
    setWallet(_wallet);
  }, [_wallet]);

  const formatDate = (date: string): string => {
    const formattedDate = new Date(date)
      .toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
      })
      .replace(/\//g, "-");
    return formattedDate;
  };

  return (
    <Modal visible={visible} cancel={cancel} ignoreBackdropClicks>
      {loading ? (
        <img
          alt="loading"
          src={resources.images.LOADING_TYPING}
          className="generic-icon xxl"
        />
      ) : (
        <section className={styles.details}>
          <div className={styles.detail}>
            <span className={styles.key}>Id:</span>
            <span className={styles.value}>{wallet?.id || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Name:</span>
            <span className={styles.value}>{wallet?.name || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Class name:</span>
            <span className={styles.value}>{wallet?.className || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Created by:</span>
            <span className={styles.value}>{wallet?.createdBy || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Created on:</span>
            <span className={styles.value}>
              {wallet?.createdOn ? formatDate(wallet.createdOn) : "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Versions:</span>
            <span className={styles.value}>{wallet?.versions || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Base Url:</span>
            <span className={styles.value}>{wallet?.baseUrl || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Balance path:</span>
            <span className={styles.value}>{wallet?.balancePath || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Place lien path:</span>
            <span className={styles.value}>
              {wallet?.placeLienPath || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Debit lien path:</span>
            <span className={styles.value}>
              {wallet?.debitLienPath || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Debit path:</span>
            <span className={styles.value}>{wallet?.debitPath || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Reversal path:</span>
            <span className={styles.value}>
              {wallet?.reversalPath || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Probe path:</span>
            <span className={styles.value}>{wallet?.probePath || "N/A"}</span>
          </div>
        </section>
      )}
    </Modal>
  );
};
export default WalletConfiguration;
